
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CircleDollarSign, Wallet, ArrowUpCircle, ArrowDownCircle, Settings, CreditCard } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import WalletRecentCharges from "@/components/masterlicense/wallet/WalletRecentCharges";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useStripePayment } from "@/hooks/stripe/useStripePayment";

interface WalletOverviewProps {
  adminId: string;
}

interface WalletData {
  id: string;
  adminId: string;
  balance: number;
  currency: string;
  autoRecharge: boolean;
  minimumBalance: number;
  autoRechargeAmount: number;
  maintainMinimumBalance: boolean;
  lastRechargeDate: string | null;
  lastRechargeAmount: number | null;
  createdAt: string;
  updatedAt: string;
}

const WalletOverview: React.FC<WalletOverviewProps> = ({ adminId }) => {
  const { toast } = useToast();
  const [wallet, setWallet] = useState<WalletData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { redirectToCheckout, isProcessing } = useStripePayment();
  
  // Dialog states
  const [fundDialogOpen, setFundDialogOpen] = useState(false);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [fundAmount, setFundAmount] = useState("100");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawMethod, setWithdrawMethod] = useState("bankAccount");
  
  useEffect(() => {
    if (adminId) {
      fetchWalletData();
    }
  }, [adminId]);

  const fetchWalletData = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('tremendous', {
        body: { 
          action: 'getAdminWallet', 
          adminId 
        }
      });
      
      if (error) throw new Error(error.message);
      
      if (data?.wallet) {
        setWallet(data.wallet);
      } else {
        // Initialize wallet if not found
        await initializeWallet();
      }
    } catch (error) {
      console.error('Error fetching wallet data:', error);
      toast({
        title: "Error",
        description: "Failed to load wallet data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const initializeWallet = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('tremendous', {
        body: { 
          action: 'initializeAdminWallet', 
          adminId,
          initialBalance: 0
        }
      });
      
      if (error) throw new Error(error.message);
      
      if (data?.wallet) {
        setWallet(data.wallet);
        toast({
          title: "Wallet Created",
          description: "Your wallet has been initialized successfully"
        });
      }
    } catch (error) {
      console.error('Error initializing wallet:', error);
      toast({
        title: "Error",
        description: "Failed to initialize wallet",
        variant: "destructive"
      });
    }
  };

  // Generate mock data for demonstration if wallet not loaded
  useEffect(() => {
    if (!isLoading && !wallet) {
      const mockWallet: WalletData = {
        id: `w_${Math.random().toString(36).substring(2, 10)}`,
        adminId,
        balance: 1000.00,
        currency: "USD",
        autoRecharge: false,
        minimumBalance: 100.00,
        autoRechargeAmount: 500.00,
        maintainMinimumBalance: true,
        lastRechargeDate: new Date(Date.now() - 604800000).toISOString(),
        lastRechargeAmount: 1000.00,
        createdAt: new Date(Date.now() - 2592000000).toISOString(),
        updatedAt: new Date(Date.now() - 86400000).toISOString()
      };
      setWallet(mockWallet);
    }
  }, [isLoading]);

  // Handle adding funds via Stripe
  const handleAddFunds = async () => {
    const amount = parseFloat(fundAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: "Invalid amount",
        description: "Please enter a valid amount to add to your wallet",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const result = await redirectToCheckout({
        amount: amount * 100, // Convert to cents for Stripe
        currency: "usd",
        description: `Add funds to Admin Wallet (${adminId})`,
        metadata: {
          adminId: adminId,
          transactionType: "wallet_funding"
        }
      });
      
      if (result.success) {
        toast({
          title: "Redirecting to payment",
          description: "You will be redirected to complete the payment"
        });
        // The page will redirect to Stripe checkout
      } else {
        throw new Error(result.error || "Failed to create checkout session");
      }
    } catch (error) {
      console.error("Error adding funds:", error);
      toast({
        title: "Payment Error",
        description: "There was an error processing your request",
        variant: "destructive"
      });
    } finally {
      setFundDialogOpen(false);
    }
  };
  
  // Handle withdrawing funds
  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: "Invalid amount",
        description: "Please enter a valid amount to withdraw",
        variant: "destructive"
      });
      return;
    }
    
    if (wallet && amount > wallet.balance) {
      toast({
        title: "Insufficient funds",
        description: "You don't have enough funds to withdraw this amount",
        variant: "destructive"
      });
      return;
    }
    
    try {
      // In a real app, this would call an API to process the withdrawal
      toast({
        title: "Withdrawal Requested",
        description: `Your withdrawal of $${amount} is being processed`,
      });
      
      // Update the wallet balance (mock)
      if (wallet) {
        setWallet({
          ...wallet,
          balance: wallet.balance - amount
        });
      }
    } catch (error) {
      console.error("Error withdrawing funds:", error);
      toast({
        title: "Withdrawal Error",
        description: "There was an error processing your withdrawal",
        variant: "destructive"
      });
    } finally {
      setWithdrawDialogOpen(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!wallet) {
    return (
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-medium">Wallet Balance</CardTitle>
          <CardDescription>Your current available funds</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <CircleDollarSign className="h-16 w-16 text-muted-foreground" />
            <p className="text-center">
              You don't have a reward wallet set up yet. Initialize your wallet to start managing funds.
            </p>
            <Button onClick={initializeWallet} disabled={isLoading}>
              {isLoading ? "Processing..." : "Initialize Wallet"}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-medium">Wallet Balance</CardTitle>
            <CardDescription>Your current available funds</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-4">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Wallet className="h-8 w-8 text-primary" />
              </div>
              <div>
                <p className="text-3xl font-bold">
                  ${wallet?.balance.toFixed(2)}
                </p>
                <p className="text-sm text-muted-foreground">
                  {wallet?.lastRechargeDate ? 
                    `Last added funds on ${new Date(wallet?.lastRechargeDate).toLocaleDateString()}` : 
                    'No recent fund additions'}
                </p>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="outline" className="flex items-center" onClick={() => setFundDialogOpen(true)}>
              <ArrowUpCircle className="mr-2 h-4 w-4" />
              Add Funds
            </Button>
            <Button variant="outline" className="flex items-center" onClick={() => setWithdrawDialogOpen(true)}>
              <ArrowDownCircle className="mr-2 h-4 w-4" />
              Withdraw
            </Button>
          </CardFooter>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-medium">Payment Methods</CardTitle>
            <CardDescription>Manage your payment options</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 border rounded-md">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-md bg-gray-100 flex items-center justify-center">
                    <CreditCard className="h-6 w-6 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium">Credit/Debit Card</p>
                    <p className="text-sm text-muted-foreground">Visa •••• 4242</p>
                  </div>
                </div>
                <div>
                  <Button variant="ghost" size="sm">Edit</Button>
                </div>
              </div>
              <Button variant="outline" className="w-full">
                <CreditCard className="mr-2 h-4 w-4" />
                Add New Payment Method
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <WalletRecentCharges adminId={adminId} />

      {/* Add Funds Dialog */}
      <Dialog open={fundDialogOpen} onOpenChange={setFundDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Funds to Wallet</DialogTitle>
            <DialogDescription>
              Enter the amount you want to add to your wallet. You'll be redirected to complete the payment.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="amount">Amount ($)</Label>
              <Input
                id="amount"
                type="number"
                min="1"
                step="0.01"
                value={fundAmount}
                onChange={(e) => setFundAmount(e.target.value)}
                placeholder="Enter amount"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFundDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddFunds} disabled={isProcessing}>
              {isProcessing ? "Processing..." : "Add Funds"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Withdraw Dialog */}
      <Dialog open={withdrawDialogOpen} onOpenChange={setWithdrawDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Withdraw Funds</DialogTitle>
            <DialogDescription>
              Enter the amount you want to withdraw and select your withdrawal method.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="withdraw-amount">Amount ($)</Label>
              <Input
                id="withdraw-amount"
                type="number"
                min="1"
                max={wallet?.balance}
                step="0.01"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder="Enter amount"
              />
              <p className="text-xs text-muted-foreground">
                Available balance: ${wallet?.balance.toFixed(2)}
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="withdraw-method">Withdraw to</Label>
              <select 
                id="withdraw-method" 
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={withdrawMethod}
                onChange={(e) => setWithdrawMethod(e.target.value)}
              >
                <option value="bankAccount">Bank Account</option>
                <option value="paypal">PayPal</option>
                <option value="creditCard">Credit Card</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWithdrawDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleWithdraw}>Withdraw Funds</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WalletOverview;
